<script lang="ts" setup>
import { computed, inject, type Ref } from 'vue'
import { debounce } from 'lodash'

import CalculationDeleteButton from '@/components/calculation-drawer/CalculationDeleteButton.vue'
import type { CalculationCPTType } from '@/stores/type/calculationCPT.type'

const props = defineProps({
  modelValue: {
    type: String,
    required: true,
    default: ''
  }
})

const calculationStore = useCalculationStore()
const calculationCPTStore = useCalculationCPTStore()
const calculationParameterStore = useCalculationParameterStore()
const authorizationStore = useAuthorizationStore()

const { selectedItems: selectedCalculations } = storeToRefs(calculationStore)
const {
  selectedItems: selectedCalculationCPTs,
  exportReportLoading,
  exportReportDialog,
  exportExcelLoading,
  exportExcelDialog
} = storeToRefs(calculationCPTStore)
const { currentItem: currentCalculationParameters } = storeToRefs(calculationParameterStore)

const emit = defineEmits(['update:modelValue'])

const dialog = inject<Ref<boolean>>('calculationsViewShowDialog')

const debouncedSearchQuery = computed({
  get() {
    return props.modelValue
  },
  set: debounce((query) => {
    emit('update:modelValue', query ?? '')
  }, 300)
})

const canAddCalculation = computed(() => {
  return authorizationStore.hasServicePermission({
    authSource: 'ProjectUser',
    allowed: ['add_calculation']
  })
})

const exportReportDisabled = computed(() => {
  if (selectedCalculationCPTs.value.length) {
    return !selectedCalculationCPTs.value.every(
      (item: CalculationCPTType) =>
        item.calculation === selectedCalculationCPTs.value[0].calculation
    )
  }
  return true
})

const exportExcelDisabled = computed(() => {
  return selectedCalculationCPTs.value.length !== 1
})

const clearFilters = () => {
  emit('update:modelValue', '')
}
</script>

<template>
  <div class="d-flex justify-center align-center ga-2 pa-2">
    <v-text-field
      v-model="debouncedSearchQuery"
      append-inner-icon="mdi-magnify"
      clearable
      color="primary"
      density="compact"
      hide-details
      label="Search..."
    ></v-text-field>
    <v-btn
      prepend-icon="mdi-close-circle-outline"
      class="text-none"
      variant="tonal"
      @click.stop="clearFilters"
    >
      Clear filters
    </v-btn>
    <v-tooltip v-if="canAddCalculation" text="Create calculation">
      <template v-slot:activator="{ props }">
        <v-btn
          :disabled="!currentCalculationParameters?.id"
          class="text-none"
          color="primary"
          prepend-icon="mdi-plus"
          v-bind="props"
          variant="tonal"
          @click="calculationStore.toggleCreateDialog()"
        >
          Create
        </v-btn>
      </template>
    </v-tooltip>
    <CalculationDeleteButton />
  </div>
  <div class="d-flex align-center ga-2 px-2 pb-2">
    <v-btn
      :disabled="!selectedCalculations.length"
      class="text-none"
      color="primary"
      hide-details
      prepend-icon="mdi-table"
      single-line
      variant="tonal"
      @click="dialog = true"
    >
      Calculation log
    </v-btn>
    <v-btn
      :disabled="exportReportDisabled"
      :loading="!exportReportDialog && exportReportLoading"
      class="text-none"
      color="primary"
      hide-details
      prepend-icon="mdi-file-export-outline"
      single-line
      variant="tonal"
      @click="calculationCPTStore.toggleExportReportDialog()"
    >
      Export report
    </v-btn>
    <v-btn
      :disabled="exportExcelDisabled"
      :loading="!exportExcelDialog && exportExcelLoading"
      class="text-none"
      color="primary"
      hide-details
      prepend-icon="mdi-file-export-outline"
      single-line
      variant="tonal"
      @click="calculationCPTStore.toggleExportExcelDialog()"
    >
      Export excel
    </v-btn>
  </div>
</template>
